import gsap from 'gsap'
import PropTypes from 'prop-types'
import { memo, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import noop from 'lib/util/noop'
import styles from './TransitionSwap.module.scss'

/**
 * The `Item`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function Item (props) {
  const {
    animateIn,
    animation,
    children,
    className,
    delayHide,
    delayShow,
    direction,
    duration,
    id,
    isShown,
    onRemoved
  } = props
  const ref = useRef()
  const wasHidden = useRef(false)
  const isMounted = useRef()

  /**
   * Handle removing the item by using the `onRemoved`
   * callback and provide the ID
   */
  const handleRemoved = useCallback(() => {
    onRemoved(id)
  }, [])

  /**
   * Show the children
   */
  const show = useCallback(
    (direction, animateIn) => {
      if (animation === 'resize') {
        gsap.set(ref.current, { opacity: 0 })

        gsap.to(ref.current, {
          opacity: 1,
          duration: animateIn ? duration : 0,
          delay: animateIn ? delayShow : 0
        })

        return
      }

      if (animation === 'accordion') {
        gsap.set(ref.current, { height: 0, opacity: 0 })

        gsap.to(ref.current, {
          height: 'auto',
          opacity: 1,
          duration: animateIn ? duration : 0,
          delay: animateIn ? delayShow : 0,
          ease: 'cubic.out'
        })

        return
      }

      const target = direction === 'up' ? '110%' : '-110%'

      gsap.set(ref.current, { y: target })
      gsap.to(ref.current, { y: '0%', duration, delay: delayShow })
    },
    [delayShow, animation]
  )

  /**
   * Hide the children
   */
  const hide = useCallback(
    direction => {
      if (animation === 'resize') {
        gsap.to(ref.current, {
          opacity: 0,
          duration,
          delay: delayHide,
          ease: 'cubic.out',
          onComplete: () => {
            if (!isMounted.current) {
              return
            }

            handleRemoved()
          }
        })

        return
      }

      if (animation === 'accordion') {
        gsap.to(ref.current, {
          height: 0,
          opacity: 0,
          duration,
          delay: delayHide,
          onComplete: () => {
            if (!isMounted.current) {
              return
            }

            handleRemoved()
          }
        })

        return
      }

      const target = direction === 'up' ? '-110%' : '110%'

      gsap.to(ref.current, {
        y: target,
        duration,
        delay: delayHide,
        onComplete: () => {
          if (!isMounted.current) {
            return
          }

          handleRemoved()
        }
      })
    },
    [delayHide, animation]
  )

  /**
   * Effect: When the `isShown` state changes and is false,
   * hide the label. This is only allowed to run once - all
   * changes are final!
   */
  useEffect(() => {
    if (isShown || wasHidden.current) {
      return
    }

    wasHidden.current = true
    hide(direction)
  }, [isShown, direction])

  /**
   * Effect: On mount/unmount
   */
  useLayoutEffect(() => {
    isMounted.current = true

    show(direction, animateIn)

    return () => {
      isMounted.current = false

      if (ref.current) {
        gsap.killTweensOf(ref.current)
      }
    }
  }, [])

  return (
    <div ref={ref} className={`${styles.swapItem} ${className}`.trimEnd()}>
      {children}
    </div>
  )
}

/** @type {object} */
Item.propTypes = {
  animateIn: PropTypes.bool,
  animation: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.node,
  className: PropTypes.string,
  isShown: PropTypes.bool,
  direction: PropTypes.string,
  onRemoved: PropTypes.func,
  duration: PropTypes.number,
  delayShow: PropTypes.number,
  delayHide: PropTypes.number
}

/** @type {object} */
Item.defaultProps = {
  animateIn: true,
  animation: 'standard',
  id: 0,
  children: [],
  className: '',
  isShown: true,
  direction: 'up',
  onRemoved: noop,
  duration: 0.25,
  delayShow: 0,
  delayHide: 0
}

// Memoize
export default memo(Item)
